import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import DefaultEditor from "react-simple-wysiwyg";
import { apiRequestHandler } from "../../apiConfig/service";
import { toast } from "react-toastify";
import { FaRegTrashCan } from "react-icons/fa6";
import { IoAdd } from "react-icons/io5";


const validationSchema = Yup.object({
  slug: Yup.string().required("Slug is required"),
  serviceName: Yup.string().required("Service name is required"),
  metaTitle: Yup.string().required("Meta title is required"),
  metaDescription: Yup.string().required("Meta description is required"),
  title: Yup.string().required("Title is required"),
  description: Yup.string().required("Description is required"),
  keywords: Yup.string().required("Keywords are required"),
  whyChooseUs: Yup.object({
    title: Yup.string().required("Why Choose Us title is required"),
    description: Yup.string().required("Why Choose Us description is required"),
  }),
  featureSection: Yup.object({
    title: Yup.string().required("Feature section title is required"),
    description: Yup.string().required("Feature section description is required"),
    features: Yup.array().of(
      Yup.string()
        .required("At least one feature is required")
        .min(1, "At least one feature is required")
    ),
  }),
  faqSection: Yup.object({
    description: Yup.string().required("FAQ section description is required"),
    questions: Yup.array()
      .of(
        Yup.object({
          question: Yup.string().required("Question is required"),
          answer: Yup.string().required("Answer is required"),
        })
      )
      .min(1, "At least one question is required"),
  }),
});

const styles = {
  formContainer: {
    minHeight: "100vh",
    padding: "20px 20px 0px 20px",
    background: "#f5f7fa",
  },
  card: {
    // backgroundColor: "#ffffff",
    borderRadius: "10px",
    padding: "0px 15px 15px 15px",
    marginBottom: "0px",
  },
  label: {
    fontWeight: 700,
    color: "#484848",
    fontSize: "14px",
    marginBottom: "0.25rem",
    display: "block",
  },
  input: {
    width: "100%",
    padding: "0.5rem 0.75rem",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "14px",
    outline: "none",
  },
  textarea: {
    width: "100%",
    padding: "0.5rem 0.75rem",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "14px",
    outline: "none",
    resize: "vertical",
    minHeight: "100px",
  },
  error: {
    color: "#e74c3c",
    fontSize: "12px",
    marginTop: "4px",
  },
  filePreviewContainer: {
    position: "relative",
    display: "inline-block",
    marginTop: "10px",
  },
  filePreview: {
    width: "100px",
    height: "100px",
    objectFit: "cover",
    borderRadius: "6px",
    marginRight: "10px",
    marginBottom: "10px",
  },
  removeIcon: {
    position: "absolute",
    top: "-8px",
    right: "2px",
    backgroundColor: "#e74c3c",
    color: "#fff",
    borderRadius: "50%",
    width: "20px",
    height: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "bold",
  },
  backBtn: {
    padding: "6px 12px",
    background: "linear-gradient(to bottom right, #163252, #0081ff)",
    color: "#fff",
    border: "1px solid #ccc",
    borderRadius: "6px",
    cursor: "pointer",
  },
  submitBtn: {
    padding: "10px 20px",
    background: "linear-gradient(to bottom right, #163252, #0081ff)",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    cursor: "pointer",
    width:"100%"
  },
  arrayBtn: {
    padding: "6px 12px",
    margin: "5px",
    borderRadius: "6px",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
  },
};

const AddServiceForm = () => {
  const { state } = useLocation();
  const service = state?.service;
  const mode = state?.mode || "add";
  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";
  const navigate = useNavigate();
  const [bannerImage, setBannerImage] = useState("");
  const [whyChooseUsImage, setWhyChooseUsImage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setBannerImage(service?.bannerImage || "");
    setWhyChooseUsImage(service?.whyChooseUs?.image || "");
  }, [service]);

  const handleImageChange = async (e, setImage, field, setFieldValue) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await apiRequestHandler({
          method: "POST",
          endPoint: "uploadFile",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response?.data?.url) {
          setImage(response.data.url);
          setFieldValue(field, response.data.url);
        } else {
          toast.error(`Failed to upload ${field}`);
        }
      } catch (err) {
        console.error("❌ Upload error:", err);
        toast.error("Image upload failed");
      }
      e.target.value = null;
    }
  };

  const handleSubmit = async (values, { resetForm }) => {
    setLoading(true);
    const payload = {
      ...values,
      bannerImage,
      whyChooseUs: { ...values.whyChooseUs, image: whyChooseUsImage },
      servicePageId: service?._id,
    };

    try {
      const res = await apiRequestHandler({
        method: "POST",
        endPoint: "addService",
        data: payload,
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (res?.success) {
        resetForm();
        setBannerImage("");
        setWhyChooseUsImage("");
        toast.success(res.message);
        navigate("/admin/list-services");
      } else {
        toast.error(res?.message || "Failed to submit service");
      }
    } catch (err) {
      toast.error("An error occurred while submitting the service");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.formContainer}>
      <div style={styles.card}>
        <Formik
          enableReinitialize
          initialValues={{
            slug: service?.slug || "",
            serviceName: service?.serviceName || "",
            metaTitle: service?.metaTitle || "",
            metaDescription: service?.metaDescription || "",
            title: service?.title || "",
            description: service?.description || "",
            keywords: service?.keywords || "",
            whyChooseUs: {
              title: service?.whyChooseUs?.title || "",
              description: service?.whyChooseUs?.description || "",
              image: service?.whyChooseUs?.image || "",
            },
            featureSection: {
              title: service?.featureSection?.title || "",
              description: service?.featureSection?.description || "",
              features: service?.featureSection?.features || [""],
            },
            faqSection: {
              description: service?.faqSection?.description || "",
              questions: service?.faqSection?.questions || [
                { question: "", answer: "" },
              ],
            },
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue, values }) => (
            <Form className="row g-4">
              {/* Section 1: Header */}
              <div className="col-12 bg-white custom-rounded">
                <div className="d-flex justify-content-between flex-row-reverse align-items-center p-3">
                  <div onClick={() => navigate(-1)} style={styles.backBtn}>← Back</div>
                  <h4 style={{ marginBottom: "0px" }}>
                    {isViewMode ? "View Service" : isEditMode ? "Edit Service" : "Add New Service"}
                  </h4>
                </div>
              </div>

              {/* Section 2: Meta Data */}
               <h5 className="custom-head">Meta Data</h5>
              <div className="col-12 bg-white p-3 custom-rounded" style={styles.card}>
               
                <div className="row g-3">
                  <div className="col-md-6">
                    <label style={styles.label}>Slug</label>
                    <Field name="slug" style={styles.input} placeholder={"Enter slug"} readOnly={isViewMode} />
                    <ErrorMessage name="slug" component="div" style={styles.error} />
                  </div>
                  <div className="col-md-6">
                    <label style={styles.label}>Meta Title</label>
                    <Field name="metaTitle" style={styles.input} placeholder={"Enter meta title"} readOnly={isViewMode} />
                    <ErrorMessage name="metaTitle" component="div" style={styles.error} />
                  </div>
                  <div className="col-md-6">
                    <label style={styles.label}>Meta Description</label>
                    <Field name="metaDescription" style={styles.input} placeholder={"Enter meta description"} readOnly={isViewMode} />
                    <ErrorMessage name="metaDescription" component="div" style={styles.error} />
                  </div>
                    <div className="col-md-6">
                    <label style={styles.label}>Keywords (comma separated)</label>
                    <Field name="keywords" style={styles.input} placeholder={"Enter keywords"} readOnly={isViewMode} />
                    <ErrorMessage name="keywords" component="div" style={styles.error} />
                  </div>
                </div>
              </div>

              {/* Section 3: Service Details */}
                  <h5 className="custom-head">Service Details</h5>
              <div className="col-12 bg-white p-3 custom-rounded" style={styles.card}>
            
                <div className="row g-3">
                  <div className="col-md-6">
                    <label style={styles.label}>Service Name</label>
                    <Field name="serviceName" style={styles.input} placeholder={"Enter service name"} readOnly={isViewMode} />
                    <ErrorMessage name="serviceName" component="div" style={styles.error} />
                  </div>
                  <div className="col-md-6">
                    <label style={styles.label}>Title</label>
                    <Field name="title" style={styles.input} placeholder={"Enter title"} readOnly={isViewMode} />
                    <ErrorMessage name="title" component="div" style={styles.error} />
                  </div>
                  <div className="col-12">
                    <label style={styles.label}>Description</label>
                    <DefaultEditor
                      value={values.description}
                      onChange={(e) => setFieldValue("description", e.target.value)}
                      disabled={isViewMode}
                    />
                    <ErrorMessage name="description" component="div" style={styles.error} />
                  </div>
                </div>
              </div>

              {/* Section 4: Images */}
               <h5 className="custom-head">Images</h5>
              <div className="col-12 bg-white p-3 custom-rounded" style={styles.card}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label style={styles.label}>Banner Image</label>
                    {!isViewMode && (
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, setBannerImage, "bannerImage", setFieldValue)}
                        style={styles.input}
                      />
                    )}
                    {bannerImage && (
                      <div style={styles.filePreviewContainer}>
                        <img src={bannerImage} alt="Banner preview" style={styles.filePreview} />
                        {!isViewMode && (
                          <div
                            style={styles.removeIcon}
                            onClick={() => {
                              setBannerImage("");
                              setFieldValue("bannerImage", "");
                            }}
                          >
                            ×
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label style={styles.label}>Why Choose Us Image</label>
                    {!isViewMode && (
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, setWhyChooseUsImage, "whyChooseUs.image", setFieldValue)}
                        style={styles.input}
                      />
                    )}
                    {whyChooseUsImage && (
                      <div style={styles.filePreviewContainer}>
                        <img src={whyChooseUsImage} alt="Why Choose Us preview" style={styles.filePreview} />
                        {!isViewMode && (
                          <div
                            style={styles.removeIcon}
                            onClick={() => {
                              setWhyChooseUsImage("");
                              setFieldValue("whyChooseUs.image", "");
                            }}
                          >
                            ×
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

            
              {/* Section 6: Why Choose Us */}
               <h5 className="custom-head">Why Choose Us</h5>
              <div className="col-12 bg-white p-3 custom-rounded" style={styles.card}>
                <div className="row g-3">
                  <div className="col-md-12">
                    <label style={styles.label}>Title</label>
                    <Field name="whyChooseUs.title" style={styles.input} placeholder={"Enter title"} readOnly={isViewMode} />
                    <ErrorMessage name="whyChooseUs.title" component="div" style={styles.error} />
                  </div>
                  <div className="col-12">
                    <label style={styles.label}>Description</label>
                    <DefaultEditor
                      value={values.whyChooseUs.description}
                      onChange={(e) => setFieldValue("whyChooseUs.description", e.target.value)}
                      disabled={isViewMode}
                    />
                    <ErrorMessage name="whyChooseUs.description" component="div" style={styles.error} />
                  </div>
                </div>
              </div>

              {/* Section 7: Feature Section */}
               <h5 className="custom-head">Feature Section</h5>

              <div className="col-12 bg-white p-3 custom-rounded" style={styles.card}>
                <div className="row g-3">
                  <div className="col-md-12">
                    <label style={styles.label}>Title</label>
                    <Field name="featureSection.title" style={styles.input} placeholder={"Enter title"} readOnly={isViewMode} />
                    <ErrorMessage name="featureSection.title" component="div" style={styles.error} />
                  </div>
                  <div className="col-12">
                    <label style={styles.label}>Description</label>
                    <DefaultEditor
                      value={values.featureSection.description}
                      onChange={(e) => setFieldValue("featureSection.description", e.target.value)}
                      disabled={isViewMode}
                    />
                    <ErrorMessage name="featureSection.description" component="div" style={styles.error} />
                  </div>
                  <FieldArray name="featureSection.features">
                    {({ push, remove }) => (
                      <div className="col-12">
                        <label style={styles.label}>Features</label>
                        {values.featureSection.features.map((_, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <Field
                              name={`featureSection.features[${index}]`}
                              style={{ ...styles.input, marginRight: "10px" }}
                              placeholder={"Enter feature"}
                              readOnly={isViewMode}
                            />
                            {!isViewMode && (
                              <>
                                {index !== 0 && (
                                  <button
                                    type="button"
                                    onClick={() => remove(index)}
                                    style={{
                                      ...styles.arrayBtn,
                                      backgroundColor: "#e74c3c",
                                      color: "#fff",
                                    }}
                                  >
                                    <FaRegTrashCan />
                                  </button>
                                )}

                                {index === values.featureSection.features.length - 1 && (
                                  <button
                                    type="button"
                                    onClick={() => push("")}
                                    style={{
                                      ...styles.arrayBtn,
                                      backgroundColor: "#333",
                                      color: "#fff",
                                    }}
                                  >
                                    <IoAdd />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        ))}

                        <ErrorMessage name="featureSection.features" component="div" style={styles.error} />
                      </div>
                    )}
                  </FieldArray>
                </div>
              </div>

              {/* Section 8: FAQ Section */}
               <h5 className="custom-head">FAQ Section</h5>

              <div className="col-12 bg-white p-3 custom-rounded" style={styles.card}>
                <div className="row g-3">
                  <div className="col-12">
                    <label style={styles.label}>Description</label>
                    <DefaultEditor
                      value={values.faqSection.description}
                      onChange={(e) => setFieldValue("faqSection.description", e.target.value)}
                      disabled={isViewMode}
                    />
                    <ErrorMessage name="faqSection.description" component="div" style={styles.error} />
                  </div>
                  <FieldArray name="faqSection.questions">
                    {({ push, remove }) => (
                      <div className="col-12">
                        <label style={styles.label}>Questions</label>
                        {values.faqSection.questions.map((_, index) => (
                          <div key={index} className="mb-3 p-3 border rounded">
                            <div className="mb-2">
                              <label style={styles.label}>Question</label>
                              <Field
                                name={`faqSection.questions[${index}].question`}
                                style={styles.input}
                                placeholder={"Enter question"}
                                readOnly={isViewMode}
                              />
                              <ErrorMessage
                                name={`faqSection.questions[${index}].question`}
                                component="div"
                                style={styles.error}
                              />
                            </div>
                            <div>
                              <label style={styles.label}>Answer</label>
                              <DefaultEditor
                                value={values.faqSection.questions[index].answer}
                                onChange={(e) =>
                                  setFieldValue(`faqSection.questions[${index}].answer`, e.target.value)
                                }
                                disabled={isViewMode}
                              />
                              <ErrorMessage
                                name={`faqSection.questions[${index}].answer`}
                                component="div"
                                style={styles.error}
                              />
                            </div>
                            {!isViewMode && (
                              <button
                                type="button"
                                onClick={() => remove(index)}
                                style={{
                                  ...styles.arrayBtn,
                                  backgroundColor: "#e74c3c",
                                  color: "#fff",
                                  marginTop: "10px",
                                }}
                              >
                                Remove FAQ
                              </button>
                            )}
                          </div>
                        ))}
                        {!isViewMode && (
                          <button
                            type="button"
                            onClick={() => push({ question: "", answer: "" })}
                            style={{
                              ...styles.arrayBtn,
                              backgroundColor: "#333",
                              color: "#fff",
                            }}
                          >
                            Add FAQ
                          </button>
                        )}
                        <ErrorMessage name="faqSection.questions" component="div" style={styles.error}>
                          {(error) =>
                            typeof error === "string"
                              ? error
                              : "Please ensure all questions and answers are filled correctly"
                          }
                        </ErrorMessage>
                      </div>
                    )}
                  </FieldArray>
                </div>
              </div>

              {/* Section 9: Submit Button */}
              {!isViewMode && (
                <div className="col-12 text-center p-3" style={styles.card}>
                  <button type="submit" style={styles.submitBtn} disabled={loading}>
                    {loading ? "Submitting..." : isEditMode ? "Update Service" : "Create Service"}
                  </button>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AddServiceForm;